/* eslint-disable react-hooks/exhaustive-deps */
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DownloadIcon from "@mui/icons-material/Download";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import LastPageRoundedIcon from "@mui/icons-material/LastPageRounded";
import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    Paper,
    styled,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Typography,
    InputLabel,
    Select,
    MenuItem,   
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { config, ERoles, formatNumberWithComma } from "../../config";
import useAuth from "../../hooks/useAuth";
import { clearErrorMessagesForBotMIS, resetUpdateBotReportStates } from "../../redux/MISReportsManagement/misReportsSlice";
import { getActiveOrgsForResellerThunk, getBotMISReports, updateBotMISReports } from "../../redux/MISReportsManagement/misReportsThunk";
import { apiGatewayAxiosInstance } from "../../utils/axios";
import RefreshButton from "../../utils/refreshButton";
import TablePaginationShowPage from "../../utils/tablePagination";
import AlertMessageComponent from "../components/AlertMessageComponent";
import FetchingSpinner from "../components/FetchingSpinner";
import NoRecords from "../components/NoRecords";
import SearchableDropdown from "./DropDownWithSearch";
import UpdateMISReportsComponent from "./UpdateMISReportsComponent";

export const calculateTime = value => {
    const time = new Date(value).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    });
    const day = new Date(value).toLocaleDateString([], { day: "2-digit" });
    const month = new Date(value).toLocaleDateString([], { month: "2-digit" });
    const year = new Date(value).toLocaleDateString([], { year: "numeric" });

    return (
        <Typography className="tableContentFontForDateAndTime">
            {day}-{month}-{year} <br />
            {time}
        </Typography>
    );
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        fontSize: 16,
        backgroundColor: theme.palette.common.lightBlue,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        border: "none",
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
    },

    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    borderCollapse: "separate",
}));

const BotMIS = () => {
    const { user } = useAuth();
    const now = dayjs();
    const state = useSelector(state => state.misReportsSlice);

    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useState({
        botName: "",
        botMessageType: "all",
    });

    const [updateReport, setUpdateReport] = useState({
        openModel: false,
        startDate: dayjs().subtract(7, "days"),
        endDate: dayjs(),
    });

    const [executedAtStartDate, setExecutedAtStartDate] = useState(
        dayjs().startOf("month").hour(now.hour()).minute(now.minute()).second(now.second()),
    );
    const [executedAtEndDate, setExecutedAtEndDate] = useState(dayjs());

    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);

    const [reload, setReload] = useState(false);
    const [errorResponse, setErrorResponse] = useState("");
    const [selectedClient, setSelectedClient] = useState("All");
    const [activeReseller, setActiveReseller] = useState("");
    const [selectedReseller, setSelectedReseller] = useState("All");
    const [availableClients, setAvailableClients] = useState(null);
    const [loadDownload, setLoadDownload] = useState(false);

    const [sumBotCounts, setSumBotCounts] = useState({
        totalRCS: 0,
        sentRCS: 0,
        deliveredRCS: 0,
        readRCS: 0,
        pendingRCS: 0,
        failedRCS: 0,
        sentRevokedRCS: 0,
        totalSMS: 0,
        sentSMS: 0,
        deliveredSMS: 0,
        failedSMS: 0,
    });

    const getTotalBotMISCounts = botReports => {
        if (botReports?.length > 0) {
            const sumOfCounts = botReports.reduce(
                (acc, item) => {
                    acc.totalRCS += item.totalRCS;
                    acc.sentRCS += item.sentRCS;
                    acc.deliveredRCS += item.deliveredRCS;
                    acc.readRCS += item.readRCS;
                    acc.pendingRCS += item.pendingRCS;
                    acc.failedRCS += item.failedRCS;
                    acc.sentRevokedRCS += item.sentRevokedRCS;
                    acc.totalSMS += item.totalSMS;
                    acc.sentSMS += item.sentSMS;
                    acc.deliveredSMS += item.deliveredSMS;
                    acc.failedSMS += item.failedSMS;
                    return acc;
                },
                {
                    totalRCS: 0,
                    sentRCS: 0,
                    deliveredRCS: 0,
                    readRCS: 0,
                    pendingRCS: 0,
                    failedRCS: 0,
                    sentRevokedRCS: 0,
                    totalSMS: 0,
                    sentSMS: 0,
                    deliveredSMS: 0,
                    failedSMS: 0,
                },
            );
            setSumBotCounts(sumOfCounts);
        }
    };

    const handleCloseUpdateReport = () => {
        dispatch(resetUpdateBotReportStates());
        setUpdateReport({ ...updateReport, openModel: false });
    };

    const handleClearUpdateReport = () => {
        setUpdateReport({
            openModel: false,
            startDate: dayjs().subtract(7, "days"),
            endDate: dayjs(),
        });
    };

    const handleChangeSearchParams = event => {
        const { name, value } = event.target;
        setSearchParams({ ...searchParams, [name]: value });
    };

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setPageSize(parseInt(event.target.value));
        setCurrentPage(0);
    };

    const onCloseErrorMessage = ({ fieldName }) => {
        dispatch(clearErrorMessagesForBotMIS({ fieldName }));
    };

    const reloadList = () => {
        setReload(!reload);
    };

    const handleClear = () => {
        setSearchParams({
            botName: "",
            botType: "ALL",
        });
        setExecutedAtStartDate(dayjs().startOf("month").hour(now.hour()).minute(now.minute()).second(now.second()));
        setExecutedAtEndDate(dayjs());
        setReload(!reload);
    };

    const handleUpdate = () => {
        dispatch(
            updateBotMISReports({
                executedAtStartDate: updateReport.startDate,
                executedAtEndDate: updateReport.endDate,
            }),
        );
    };

    const getActiveReseller = async () => {
        try {
            const response = await apiGatewayAxiosInstance.get(`/user_management/reseller/get-active-reseller`);
            setActiveReseller(response.data.data);
        } catch (error) {
            console.error("Error fetching active reseller:", error);
        }
    };

    const getActiveClients = async () => {
        try {
            const response = await apiGatewayAxiosInstance.get(`/user_management/organization/get-all-active-org-by-registryId`, {
                params: {
                    registryId: selectedReseller,
                },
            });
            setAvailableClients(response.data.data);
        } catch (error) {
            console.error("Error fetching active reseller:", error);
        }
    };

    const downloadCSV = async () => {
        try {
            setLoadDownload(true);

            const response = await apiGatewayAxiosInstance.get("/mis_server/bot/download-bot-mis-report", {
                params: {
                    roleName: user?.roleName,
                    registryId: selectedReseller,
                    orgId: selectedClient,
                    startDate: new Date(executedAtStartDate),
                    endDate: new Date(executedAtEndDate),
                    botName: searchParams.botName,
                    botMessageType: searchParams.botMessageType,
                },
            });
            if (response.status === 200) {
                const csvBlob = new Blob([response.data.reports], {
                    type: "text/csv",
                });
                const csvUrl = URL.createObjectURL(csvBlob);

                const link = document.createElement("a");
                link.href = csvUrl;
                link.setAttribute("download", `bot_mis.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Error downloading CSV: ", error);
        }
        setLoadDownload(false);
    };

    useEffect(() => {
        if (user?.roleName.toLowerCase() === ERoles.SUPER_ADMIN) {
            // if (selectedReseller) {
            if (!selectedClient && selectedReseller) {
                getActiveClients();
            }
            const registryId = selectedReseller && selectedReseller !== "All" ? selectedReseller : null;
            const orgId = selectedClient && selectedClient !== "All" ? selectedClient : null;
            dispatch(
                getBotMISReports({
                    roleName: user?.roleName,
                    registryId,
                    currentPage: currentPage + 1,
                    pageSize,
                    orgId: orgId,
                    botName: searchParams.botName,
                    botMessageType: searchParams.botMessageType,
                    startDate: new Date(executedAtStartDate),
                    endDate: new Date(executedAtEndDate),
                }),
            );
            // } else {
            //     getActiveReseller();
            // }
        } else if (user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN) {
            if (selectedClient) {
                dispatch(
                    getBotMISReports({
                        roleName: user?.roleName,
                        registryId: selectedReseller,
                        currentPage: currentPage + 1,
                        pageSize,
                        orgId: selectedClient,
                        botName: searchParams.botName,
                        botMessageType: searchParams.botMessageType,
                        startDate: new Date(executedAtStartDate),
                        endDate: new Date(executedAtEndDate),
                    }),
                );
            } else {
                dispatch(getActiveOrgsForResellerThunk());
            }
        } else {
            dispatch(
                getBotMISReports({
                    roleName: user?.roleName,
                    registryId: selectedReseller,
                    currentPage: currentPage + 1,
                    pageSize,
                    orgId: user?.orgId,
                    botName: searchParams.botName,
                    botMessageType: searchParams.botMessageType,
                    startDate: new Date(executedAtStartDate),
                    endDate: new Date(executedAtEndDate),
                }),
            );
        }
        // getTotalBotMISCounts(state.botMISReports);
    }, [currentPage, pageSize, reload, selectedClient, selectedReseller]);

    useEffect(() => {
        getTotalBotMISCounts(state.botMISReports);
        getActiveReseller();
    }, [state.getBotMISReportsLoading]);

    return (
        <Grid>
            <Grid container mb={4} className="listHeader">
                <Grid item md={6} className="align-items-center">
                    <Typography variant="h3">
                        <b>Bot MIS</b>
                    </Typography>
                </Grid>

                <Grid item md={6} className="listHeaderFilter">
                    <Grid display={"flex"} flexDirection={"row"} gap={2}>
                        <RefreshButton loading={state.getBotMISReportsLoading} onClick={reloadList} />
                        {(user?.roleName === ERoles.RESELLER_ADMIN || user?.roleName === ERoles.SUPER_ADMIN) && (
                            <Button
                                sx={{ marginRight: "10px" }}
                                variant="contained"
                                onClick={() => setUpdateReport({ ...updateReport, openModel: true })}
                            >
                                Update
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Card className="border border-secondary-50">
                <CardContent
                    sx={{
                        padding: 0,
                    }}
                >
                    {user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN && (
                        <Grid padding={3}>
                            <FormControl size="small" fullWidth>
                                <SearchableDropdown
                                    dropDownList={state.activeOrgList}
                                    value={selectedClient || "All"}
                                    handleChange={e => {
                                        const value = e.target.value;
                                        setCurrentPage(0);
                                        setPageSize(10);
                                        setSelectedClient(e.target.value);
                                    }}
                                    disabled={state.activeOrgLoading}
                                    label={"Select Client"}
                                    size="small"
                                    name="Active Clients"
                                    placeholder="Clients"
                                    valueKey={"_id"}
                                    dropDownValueKey={"orgName"}
                                />
                            </FormControl>
                        </Grid>
                    )}

                    {user?.roleName.toLowerCase() === ERoles.SUPER_ADMIN && (
                        <Grid
                            sx={{
                                padding: 3,
                                display: "flex",
                                flexDirection: "row",
                                gap: 2,
                                "@media (max-width: 600px)": {
                                    flexDirection: "column",
                                },
                            }}
                        >
                            <FormControl size="small" fullWidth>
                                <SearchableDropdown
                                    label="Select Reseller"
                                    placeholder="Reseller"
                                    name="Active Reseller"
                                    size="small"
                                    disabled={state.activeOrgLoading}
                                    value={selectedReseller || "All"}
                                    dropDownList={activeReseller || []}
                                    handleChange={e => {
                                        const value = e.target.value;
                                        setCurrentPage(0);
                                        setPageSize(10);
                                        setSelectedReseller(value === "All" ? "" : value);
                                        if (value === "All") {
                                            setSelectedClient("All");
                                        } else {
                                            setSelectedClient("");
                                        }
                                    }}
                                    valueKey={"_id"}
                                    dropDownValueKey={"resellerName"}
                                />
                            </FormControl>

                            {config.REACT_APP_TO_SHOW_MIS_REPORTS_TO_SUPER_ADMIN === "true" && (
                                <FormControl size="small" fullWidth>
                                    <SearchableDropdown
                                        label="Select Clients"
                                        placeholder="Clients"
                                        name="Active Clients"
                                        size="small"
                                        disabled={state.activeOrgLoading || !selectedReseller || selectedReseller === "All"}
                                        value={selectedClient || "All"}
                                        dropDownList={availableClients || []}
                                        handleChange={e => {
                                            const value = e.target.value;
                                            setCurrentPage(0);
                                            setPageSize(10);
                                            setSelectedClient(value);
                                        }}
                                        valueKey={"_id"}
                                        dropDownValueKey={"orgName"}
                                    />
                                </FormControl>
                            )}
                        </Grid>
                    )}

                    {user?.roleName.toLowerCase() === ERoles.RESELLER_ADMIN && !selectedClient ? (
                        <Typography color="black" fontWeight={500} textAlign={"center"}>
                            Select Client
                        </Typography>
                    ) : (
                        <>
                            <Grid
                                sx={{
                                    // display: "flex",
                                    // padding: 3,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    flexDirection: "row",
                                    gap: 2,
                                    padding: 5,
                                }}
                            >
                                <Grid
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        gap: 5,
                                    }}
                                >
                                    <Grid>
                                        <TextField
                                            size="small"
                                            label="Bot Name"
                                            id="botName"
                                            placeholder="Bot Name"
                                            name="botName"
                                            value={searchParams.botName}
                                            onChange={handleChangeSearchParams}
                                        />
                                    </Grid>
                                    <Grid>
                                        {/* dropdown for bot type (promotional/transactional, by default show all) */}
                                        <FormControl sx={{ minWidth: 150 }} size="medium">
                                            <InputLabel id="bot-type-label">Select Bot Type</InputLabel>
                                            <Select
                                                labelId="bot-type-label"
                                                id="bot-type-select"
                                                value={searchParams.botMessageType}
                                                label="Select Bot Type"
                                                name="botMessageType"
                                                onChange={handleChangeSearchParams}
                                                size="small"
                                            >
                                                <MenuItem value={"all"}>All</MenuItem>
                                                <MenuItem value={"promotional"}>Promotional</MenuItem>
                                                <MenuItem value={"transactional"}>Transactional</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="Start Date"
                                                value={executedAtStartDate}
                                                onChange={value => setExecutedAtStartDate(dayjs(value))}
                                                format="DD-MM-YYYY"
                                                name="startDate"
                                                disableFuture
                                                sx={{
                                                    "& .MuiInputBase-root": {
                                                        height: "36px", // Adjust height for DatePicker
                                                        padding: "0 10px", // Adjust padding
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="End Date"
                                                value={executedAtEndDate}
                                                onChange={value => setExecutedAtEndDate(dayjs(value))}
                                                format="DD-MM-YYYY"
                                                name="startDate"
                                                disableFuture
                                                sx={{
                                                    "& .MuiInputBase-root": {
                                                        height: "36px", // Adjust height for DatePicker
                                                        padding: "0 10px", // Adjust padding
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                </Grid>
                                <Grid
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Button
                                        onClick={handleClear}
                                        color="error"
                                        variant="contained"
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: 1,
                                            padding: "6px 16px", // Adjust button padding
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: 1,
                                            padding: "6px 16px", // Adjust button padding
                                        }}
                                        onClick={() => setReload(!reload)}
                                    >
                                        Search
                                    </Button>
                                    <Button onClick={downloadCSV} disabled={loadDownload || state?.botMISReports?.length <= 0}>
                                        {loadDownload ? <CircularProgress size={15} className="spinner" /> : <DownloadIcon />}
                                        &nbsp; Download Report
                                    </Button>
                                </Grid>
                            </Grid>

                            {state.getBotMISReportsLoading ? (
                                <FetchingSpinner />
                            ) : state.botMISReports && state?.botMISReports?.length > 0 ? (
                                <StyledTableContainer component={Paper} className="table-container" sx={{ maxHeight: "70vh" }}>
                                    <Table size="small" stickyHeader aria-label="simple table">
                                        <TableHead>
                                            <TableRow>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    Sr No
                                                </StyledTableCell>
                                                {user?.roleName === ERoles.SUPER_ADMIN && !selectedReseller && (
                                                    // {!selectedReseller && !(user?.roleName === ERoles.ORG_ADMIN) && (
                                                    <StyledTableCell align="left" className="col-1 tableHeaderFont">
                                                        <Typography>Reseller Name</Typography>
                                                    </StyledTableCell>
                                                )}
                                                {(!selectedClient || selectedClient === "All") && !(user?.roleName === ERoles.ORG_ADMIN) && (
                                                    <StyledTableCell align="left" className="col-1 tableHeaderFont">
                                                        <Typography>Organization Name</Typography>
                                                    </StyledTableCell>
                                                )}

                                                <StyledTableCell className="tableHeaderFont" align="left">
                                                    Bot Name
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    Date
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    Total RCS
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Sent
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Delivered
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Read
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Pending
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Failed
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    RCS Sent Revoked
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    Total SMS
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    SMS Sent
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    SMS Delivered
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    SMS Failed
                                                </StyledTableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {state.botMISReports.length > 0 &&
                                                state.botMISReports?.map((bot, index) => (
                                                    <StyledTableRow key={index}>
                                                        <StyledTableCell align="center">{currentPage * pageSize + index + 1}</StyledTableCell>

                                                        {user?.roleName === ERoles.SUPER_ADMIN && !selectedReseller && (
                                                            // {!selectedReseller && !(user?.roleName === ERoles.ORG_ADMIN) && (

                                                            <StyledTableCell align="left">{bot.resellerName}</StyledTableCell>
                                                        )}
                                                        {(!selectedClient || selectedClient === "All") && !(user?.roleName === ERoles.ORG_ADMIN) && (
                                                            <StyledTableCell align="left">{bot.orgName}</StyledTableCell>
                                                        )}

                                                        <StyledTableCell align="left">{bot.botName}</StyledTableCell>
                                                        <StyledTableCell align="center">
                                                            {bot.reportDate ? dayjs(bot.reportDate).format("YYYY-MM-DD") : "-"}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.totalRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.sentRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.deliveredRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.readRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.pendingRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.failedRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.sentRevokedRCS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.totalSMS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.sentSMS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.deliveredSMS)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className="tableContentFontForDateAndTime" align="center">
                                                            {formatNumberWithComma(bot.failedSMS)}
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                ))}
                                        </TableBody>
                                        <TableFooter
                                            className="sticky-footer"
                                            sx={{
                                                position: "sticky",
                                                bottom: 0,
                                                backgroundColor: "red",
                                                padding: 0,
                                                margin: 0,
                                            }}
                                        >
                                            <TableRow>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {"Total: "}
                                                </StyledTableCell>
                                                {(!selectedClient || selectedClient === "All") && !(user?.roleName === ERoles.ORG_ADMIN) && (
                                                    <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                )}
                                                {user?.roleName === ERoles.SUPER_ADMIN && !selectedReseller && (
                                                    <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                )}

                                                {/* {!selectedReseller && !(user?.roleName === ERoles.ORG_ADMIN) && <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>} */}
                                                <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center"></StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.totalRCS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.sentRCS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.deliveredRCS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.readRCS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.pendingRCS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.failedRCS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.sentRevokedRCS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.totalSMS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.sentSMS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.deliveredSMS) ?? "-"}
                                                </StyledTableCell>
                                                <StyledTableCell className="tableHeaderFont" align="center">
                                                    {formatNumberWithComma(sumBotCounts.failedSMS) ?? "-"}
                                                </StyledTableCell>
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                </StyledTableContainer>
                            ) : (
                                <NoRecords />
                            )}

                            {state.botMISReportsCount > 0 && (
                                <Grid
                                    className="my-2"
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        padding: 10,
                                    }}
                                >
                                    <Grid
                                        style={{
                                            marginTop: "15px",
                                        }}
                                    >
                                        Total: {state.botMISReportsCount}
                                    </Grid>

                                    <TablePaginationShowPage
                                        rowsPerPageOptions={[5, 10, 25, 100, 500, 1000]}
                                        colSpan={3}
                                        count={state.botMISReportsCount}
                                        rowsPerPage={pageSize}
                                        page={currentPage}
                                        slotProps={{
                                            select: {
                                                "aria-label": "Rows per page",
                                            },
                                            actions: {
                                                showFirstButton: true,
                                                showLastButton: true,
                                                slots: {
                                                    firstPageIcon: FirstPageRoundedIcon,
                                                    lastPageIcon: LastPageRoundedIcon,
                                                    nextPageIcon: ChevronRightRoundedIcon,
                                                    backPageIcon: ChevronLeftRoundedIcon,
                                                },
                                            },
                                        }}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                    />
                                    <AlertMessageComponent
                                        message={errorResponse}
                                        fieldName={"ErrorMessage"}
                                        handleClose={() => setErrorResponse("")}
                                        show={Boolean(errorResponse)}
                                    />
                                </Grid>
                            )}
                        </>
                    )}
                    {updateReport.openModel && (
                        <UpdateMISReportsComponent
                            open={updateReport.openModel}
                            handleClose={handleCloseUpdateReport}
                            setUpdateDates={setUpdateReport}
                            updateDates={updateReport}
                            handleClear={handleClearUpdateReport}
                            handleUpdate={handleUpdate}
                            loading={state.updateBotMISReportsLoading}
                            error={state.updateBotMISReportsError}
                            success={state.updateBotMISReportsMessage}
                        />
                    )}
                    {state.getBotMISReportsError && (
                        <AlertMessageComponent
                            message={state.getBotMISReportsError}
                            fieldName={"getBotMISReportsError"}
                            handleClose={onCloseErrorMessage}
                            show={Boolean(state.getBotMISReportsError)}
                        />
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
};

export default BotMIS;
